import { AuthContext, ModalContext } from "context/providers";
import { EMPLOYMENT_TYPES, JOB_OPPORTUNITY_TIME_COMMITMENT } from "lookup";
import React, { useContext, useMemo } from "react";
import {
  generateJobExternalLink,
  getJobOrganization,
} from "utils/helpers/jobOppDetails";
import { DMPBadge, ExistingCustomerBadge } from "components/Badges";
import { JobField } from "components/JobDetailsPageHeader/molecules/JobField";
import { Button } from "components/ui/button";
import StatusField from "components/JobDetailsPageHeader/molecules/StatusField";
import RopesAssessmentDialog from "components/JobDetailsPageHeader/RopesAssessmentDialog";

const JobDetailsPageHeader = ({ user, jobOpp }) => {
  const { showModal } = useContext(ModalContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const timezoneOverlap = useMemo(() => {
    if (jobOpp.timeOverlap > 1 && jobOpp.timeOverlap < 8) {
      return `${jobOpp.timeOverlap} hours`;
    } else if (jobOpp.timeOverlap >= 8) {
      return "All hours";
    }

    return "No Restriction";
  }, [jobOpp.timeOverlap]);

  const getTimeCommitmentLabel = (jobOpp) => {
    if (jobOpp.timeCommitment === JOB_OPPORTUNITY_TIME_COMMITMENT.FULLTIME) {
      return "Full-time";
    }

    return "Part-time";
  };

  const getJobLength = (jobOpp) => {
    if (jobOpp?.jobLengthInWeeks) {
      return `${jobOpp.jobLengthInWeeks}
          ${jobOpp.jobLengthInWeeks > 1 ? "weeks" : "week"}`;
    }

    if (jobOpp?.jobLength) {
      return `${jobOpp?.jobLength} ${
        jobOpp?.jobLength > 1 ? "months" : "month"
      }`;
    }

    return "";
  };

  const getStartDate = (jobOpp) => {
    const startDate = new Date(jobOpp.startDate);
    if (startDate <= new Date()) {
      return "Immediate";
    }

    const year = startDate.getFullYear();
    const month = (1 + startDate.getMonth()).toString().padStart(2, "0");
    const day = startDate.getDate().toString().padStart(2, "0");

    return month + "/" + day + "/" + year;
  };

  const jobExternalLink = generateJobExternalLink(jobOpp.sfdcExternalID);

  const geographicalRegionsSorted = [
    ...(jobOpp.geographicalRegions || []),
  ]?.sort((a, b) => {
    if (a.countryNames.length === 0 && b.countryNames.length === 0) {
      return 0;
    } else if (a.countryNames.length === 0) {
      return -1;
    }
    return 1;
  });

  const getGeographicalRegions = () => {
    return geographicalRegionsSorted
      .map(({ regionName, countryNames }, index) => {
        const fallback = jobOpp.geographicalRegions.find(
          (r) => r.regionName === regionName
        );
        const fullCountries =
          countryNames.length === 0
            ? fallback?.countryNames || []
            : countryNames;

        const isLast = index === geographicalRegionsSorted.length - 1;
        const comma = isLast ? "" : ", ";

        if (fullCountries.length === 0) {
          return regionName.toUpperCase() + comma;
        }

        if (fullCountries.length <= 2) {
          return fullCountries.join(", ") + comma;
        }

        const preview = fullCountries.slice(0, 2).join(", ");
        return `${regionName.toUpperCase()} (${preview}, ...)${comma}`;
      })
      .join("");
  };

  const getLocation = () => {
    const { cityName, stateName, countryName } = jobOpp.location ?? {};

    return [cityName, stateName, countryName].filter(Boolean).join(", ");
  };

  return (
    <div className="min-h-96 w-full px-4 md:px-14 pt-12">
      <div className="w-full h-full bg-white flex flex-col gap-y-6 pb-2">
        {jobOpp?.id && (
          <div className="flex flex-col gap-y-6">
            <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
              <h1
                className="text-lg md:text-4xl font-nexa font-bold"
                data-testid="job-details-title"
              >
                {jobOpp.title}
              </h1>
              {jobOpp.isDMP && (
                <div className="flex items-center gap-x-2">
                  <DMPBadge />
                  {jobOpp.customer?.sfdcCustomerStatus ===
                    "EXISTING_CUSTOMER" && <ExistingCustomerBadge />}
                </div>
              )}
              <StatusField
                status={jobOpp?.status}
                hasPrimaryMatcher={!!jobOpp.torcOwner?.username}
              />

              <div className="ml-auto flex items-center gap-x-2">
                {loggedInUser.canAccessTVA && (
                  <Button variant="outline" asChild>
                    <a
                      href={`${process.env.REACT_APP_TVA_DOMAIN}/jobs/${jobOpp.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View TVA
                    </a>
                  </Button>
                )}

                <RopesAssessmentDialog />

                <Button
                  variant="default"
                  onClick={() => showModal({ type: "jobModal", user })}
                >
                  Open Details
                </Button>
              </div>
            </div>

            <div className="w-full flex justify-start gap-x-1 md:gap-x-2 lg:gap-x-4 gap-y-4">
              <JobField
                label="Customer Name"
                value={getJobOrganization(jobOpp)}
              />
              <JobField
                label="Duration"
                value={`${getTimeCommitmentLabel(jobOpp)} | ${getJobLength(
                  jobOpp
                )}`}
              />
              <JobField label="Start Date" value={getStartDate(jobOpp)} />
              <JobField
                label="Timezone"
                value={jobOpp.timezone?.label || "-"}
              />
              <JobField label="Timezone Overlap" value={timezoneOverlap} />
              {jobOpp.sfdcExternalID ? (
                <JobField
                  label="External Job ID"
                  value={jobOpp.externalJobId}
                  valueHref={jobExternalLink}
                />
              ) : null}
              {jobOpp.requiredExperience && (
                <JobField
                  label="Required Overall Experience"
                  value={jobOpp.requiredExperience}
                />
              )}
              {jobOpp.employmentType && (
                <JobField
                  label="Employment"
                  value={EMPLOYMENT_TYPES[jobOpp.employmentType]}
                />
              )}
              {jobOpp.geographicalRegions?.length > 0 && (
                <JobField
                  value={getGeographicalRegions()}
                  label="Geographical Regions"
                />
              )}
              {jobOpp.location && (
                <JobField value={getLocation()} label="Location" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailsPageHeader;
